import { useRef } from 'react';

import type { StandbyPublicSettings } from '@/api/standby/standbyPublicShopQuery/types';
import { useStandbyPublicShopQuery } from '@/hooks/queries/standby/useStandbyPublicShopQuery';
import {
  type StandbyFlagGateStatus,
  useStandbyFlagGate,
} from '@/hooks/standby/useStandbyFlagGate';

export const getStandbyWaitlistMode = (
  flagStatus: StandbyFlagGateStatus,
  settings: StandbyPublicSettings | undefined,
): boolean =>
  flagStatus === 'open' &&
  !!settings &&
  settings.available !== false &&
  settings.standbyEnabled &&
  settings.waitlistEnabled !== false;

export const useStandbyWaitlistMode = (shopId: string): boolean => {
  const flagStatus = useStandbyFlagGate();
  // Same read as the standby [shopId] layout: a saved "on" can be stale, so
  // only this visit's own read decides.
  const { data, isSuccess, isFetching, isFetchedAfterMount } =
    useStandbyPublicShopQuery(shopId, {
      enabled: flagStatus === 'open',
      staleTime: 0,
      refetchOnWindowFocus: false,
    });

  // Latched like useStandbyFlagGate: a refetch mid-checkout must not swap the
  // waitlist UI under the user; the backend refuses a join if standby went off.
  const wasOnRef = useRef(false);
  if (wasOnRef.current) return true;

  const isOn = getStandbyWaitlistMode(
    flagStatus,
    isSuccess && isFetchedAfterMount && !isFetching
      ? data?.settings
      : undefined,
  );
  if (isOn) wasOnRef.current = true;
  return isOn;
};
